import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { Button, Input, cn } from '@respectly/ui';

/**
 * An ordered list of short strings.
 *
 * Used for sample prompts, quick replies and allowed origins. Order matters for
 * the first two — the widget renders sample prompts top to bottom and the first
 * one is the one most visitors click — so this offers explicit reordering rather
 * than a tag input that treats the list as a set.
 */
export function StringListEditor({
  value,
  onChange,
  placeholder,
  addLabel = 'Add',
  max,
  maxLength,
  emptyHint,
  monospace,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  addLabel?: string;
  max?: number;
  maxLength?: number;
  emptyHint?: string;
  monospace?: boolean;
}) {
  const [pending, setPending] = useState('');
  const atLimit = max != null && value.length >= max;

  const add = () => {
    const trimmed = pending.trim();
    if (!trimmed || atLimit) return;
    if (value.includes(trimmed)) {
      setPending('');
      return;
    }
    onChange([...value, trimmed]);
    setPending('');
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {value.length === 0 && emptyHint && (
        <p className="text-xs text-slate-400 dark:text-slate-500">{emptyHint}</p>
      )}

      {value.map((entry, index) => (
        <div key={`${entry}-${index}`} className="flex items-center gap-1.5">
          <Input
            value={entry}
            maxLength={maxLength}
            onChange={(e) => {
              const next = [...value];
              next[index] = e.target.value;
              onChange(next);
            }}
            className={cn('flex-1', monospace && 'font-mono text-xs')}
          />
          <div className="flex">
            <button
              type="button"
              aria-label={`Move "${entry}" up`}
              disabled={index === 0}
              onClick={() => move(index, -1)}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={`Move "${entry}" down`}
              disabled={index === value.length - 1}
              onClick={() => move(index, 1)}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={`Remove "${entry}"`}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}

      {!atLimit && (
        <div className="flex items-center gap-1.5">
          <Input
            value={pending}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(e) => setPending(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            className={cn('flex-1', monospace && 'font-mono text-xs')}
          />
          <Button type="button" variant="outline" size="sm" onClick={add} disabled={!pending.trim()}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            {addLabel}
          </Button>
        </div>
      )}

      {atLimit && (
        <p className="text-xs text-slate-400 dark:text-slate-500">
          {max} is the maximum — remove one to add another.
        </p>
      )}
    </div>
  );
}
